/**
 * Normalize estimated operating hours string to total hours for the study period.
 * Supports "X hrs/week" or "X hrs/month". Defaults to 0 if format is unrecognized.
 */
export function normalizeOperatingHours(estimatedOperatingHours, studyDurationMonths) {
  if (!estimatedOperatingHours || !studyDurationMonths) return 0;
  const weekMatch = estimatedOperatingHours.match(/([\d.]+)\s*hrs?\s*\/\s*week/i);
  const monthMatch = estimatedOperatingHours.match(/([\d.]+)\s*hrs?\s*\/\s*month/i);
  if (weekMatch) {
    const hoursPerWeek = parseFloat(weekMatch[1]);
    return hoursPerWeek * (studyDurationMonths * 4.33); // ~4.33 weeks/month
  }
  if (monthMatch) {
    const hoursPerMonth = parseFloat(monthMatch[1]);
    return hoursPerMonth * studyDurationMonths;
  }
  // Try to parse as just a number (total hours)
  const num = parseFloat(estimatedOperatingHours);
  return isNaN(num) ? 0 : num;
}

/**
 * Calculate individual equipment consumption (kWh) for the study period.
 * @param {Object} equipment - Equipment object with powerRating, estimatedOperatingHours, loadFactor, etc.
 * @param {number} studyDurationMonths - Duration of study period in months.
 * @returns {number} consumptionKWh
 */
export function calculateIndividualEquipmentConsumption(equipment, studyDurationMonths) {
  if (!equipment || !studyDurationMonths) return 0;
  const powerRating = parseFloat(equipment.powerRating) || 0;
  const loadFactor = parseFloat(equipment.loadFactor) || 0;
  const totalOperatingHours = normalizeOperatingHours(equipment.estimatedOperatingHours, studyDurationMonths);
  return powerRating * totalOperatingHours * (loadFactor / 100);
}

/**
 * Aggregate consumption by usage categories.
 * @param {Array} equipmentList - List of equipment objects.
 * @param {number} studyDurationMonths - Duration of study period in months.
 * @param {Array} usageCategoriesConstant - Array of usage category objects (with id, label, isExempt).
 * @returns {Object} categoryTotals
 */
export function aggregateConsumptionByCategories(equipmentList, studyDurationMonths, usageCategoriesConstant) {
  const categoryTotals = { totalKWh: 0 };
  usageCategoriesConstant.forEach(cat => {
    categoryTotals[cat.id] = { kWh: 0, isExempt: !!cat.isExempt, label: cat.label };
  });

  if (!Array.isArray(equipmentList)) return categoryTotals;

  equipmentList.forEach(equipment => {
    const usageCategory = equipment.usageCategory || usageCategoriesConstant[0].id;
    const kWh = calculateIndividualEquipmentConsumption(equipment, studyDurationMonths);
    if (categoryTotals[usageCategory]) {
      categoryTotals[usageCategory].kWh += kWh;
    } else {
      // fallback for unknown category
      categoryTotals[usageCategory] = { kWh, isExempt: false, label: usageCategory };
    }
    categoryTotals.totalKWh += kWh;
  });

  return categoryTotals;
}

/**
 * Determine predominant use based on aggregated consumption.
 * @param {Object} aggregatedConsumption - Output from aggregateConsumptionByCategories.
 * @param {number} predominantUseThreshold - Threshold percentage for exemption (default 50).
 * @returns {Object} { exemptPercentage, isPredominantlyExempt, details }
 */
export function determinePredominantUse(aggregatedConsumption, predominantUseThreshold = 50) {
  if (!aggregatedConsumption || typeof aggregatedConsumption.totalKWh !== 'number' || aggregatedConsumption.totalKWh === 0) {
    return { exemptPercentage: 0, isPredominantlyExempt: false, details: aggregatedConsumption };
  }
  let totalExemptKWh = 0;
  Object.keys(aggregatedConsumption).forEach(key => {
    if (key === 'totalKWh') return;
    const cat = aggregatedConsumption[key];
    if (cat.isExempt) totalExemptKWh += cat.kWh;
  });
  const exemptPercentage = (totalExemptKWh / aggregatedConsumption.totalKWh) * 100;
  return {
    exemptPercentage,
    isPredominantlyExempt: exemptPercentage >= predominantUseThreshold,
    details: aggregatedConsumption
  };
}
